import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Tooltip,
} from '@mui/material';
import { Add, ArrowBack, CheckCircle, Refresh, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('es-EC') : '—';

const ESTADO_COLORS = {
  PENDIENTE: { label: 'Pendiente', bg: '#fff8e1', color: '#f57f17' },
  COBRADO:   { label: 'Cobrado',   bg: '#e8f5e9', color: '#2e7d32' },
  PROTESTADO:{ label: 'Protestado',bg: '#fce4ec', color: '#b71c1c' },
  ANULADO:   { label: 'Anulado',   bg: '#f5f5f5', color: '#757575' },
};

const EMPTY_FORM = {
  tipo: 'RECIBIDO',
  numero_cheque: '',
  banco_id: '',
  fecha_emision: '',
  fecha_cobro: '',
  monto: '',
  beneficiario: '',
  concepto: '',
};

export default function Cheques() {
  const navigate = useNavigate();
  const [cheques, setCheques] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null); // cheque a editar estado
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => { cargarTodo(); }, []);

  const cargarTodo = async () => {
    try {
      setLoading(true);
      const [chRes, bkRes] = await Promise.all([
        axios.get(`${API}/api/tesoreria/cheques`, cfg()),
        axios.get(`${API}/api/tesoreria/bancos`, cfg()),
      ]);
      setCheques(chRes.data.data || []);
      setBancos(bkRes.data.data || []);
    } catch (e) {
      setError('Error al cargar cheques');
    } finally {
      setLoading(false);
    }
  };

  const guardarCheque = async () => {
    if (!form.numero_cheque || !form.monto || !form.fecha_emision) return;
    try {
      setSaving(true);
      await axios.post(`${API}/api/tesoreria/cheques`, form, cfg());
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      cargarTodo();
    } catch (e) {
      setError(e.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const cambiarEstado = async (cheque, nuevoEstado) => {
    try {
      await axios.put(`${API}/api/tesoreria/cheques/${cheque.id}`, { estado: nuevoEstado }, cfg());
      cargarTodo();
    } catch (e) {
      setError('Error al actualizar estado');
    }
  };

  const pendientes = cheques.filter(c => c.estado === 'PENDIENTE');
  const totalPendiente = pendientes.reduce((s, c) => s + parseFloat(c.monto || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#e65100' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/tesoreria')} size="small" sx={{ border: '1px solid #e0e0e0' }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#e65100' }}>📋 Cheques</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {pendientes.length} pendiente(s) · {fmt(totalPendiente)} por cobrar
          </Typography>
        </Box>
        <Button size="small" startIcon={<Refresh />} onClick={cargarTodo} sx={{ textTransform: 'none', color: '#e65100', border: '1px solid #ffccbc' }}>
          Actualizar
        </Button>
        <Button
          variant="contained" startIcon={<Add />}
          onClick={() => { setForm(EMPTY_FORM); setDialogOpen(true); }}
          sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, textTransform: 'none' }}
        >
          Nuevo Cheque
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Tabla */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#fff3e0', fontWeight: 700, fontSize: 12 } }}>
                <TableCell>N° Cheque</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Banco</TableCell>
                <TableCell>Beneficiario</TableCell>
                <TableCell>F. Emisión</TableCell>
                <TableCell>F. Cobro</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell>Concepto</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cheques.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5, color: 'text.secondary', fontSize: 13 }}>
                    Sin cheques registrados
                  </TableCell>
                </TableRow>
              ) : (
                cheques.slice().reverse().map(c => {
                  const banco = bancos.find(b => b.id === c.banco_id);
                  const est = ESTADO_COLORS[c.estado] || ESTADO_COLORS.PENDIENTE;
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700 }}>{c.numero_cheque}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        <Chip label={c.tipo} size="small" sx={{ fontSize: 10, height: 18, bgcolor: c.tipo === 'RECIBIDO' ? '#e3f2fd' : '#f3e5f5', color: c.tipo === 'RECIBIDO' ? '#1565c0' : '#6a1b9a' }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{banco?.nombre_banco || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{c.beneficiario || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtDate(c.fecha_emision)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{fmtDate(c.fecha_cobro)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: '#e65100' }}>{fmt(c.monto)}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.concepto || '—'}</TableCell>
                      <TableCell>
                        <Chip label={est.label} size="small" sx={{ fontSize: 10, height: 20, bgcolor: est.bg, color: est.color, fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="center">
                        {c.estado === 'PENDIENTE' && (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Marcar cobrado">
                              <IconButton size="small" onClick={() => cambiarEstado(c, 'COBRADO')}>
                                <CheckCircle sx={{ fontSize: 16, color: '#43a047' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Marcar protestado">
                              <IconButton size="small" onClick={() => cambiarEstado(c, 'PROTESTADO')}>
                                <span style={{ fontSize: 14 }}>⛔</span>
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog nuevo cheque */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Nuevo Cheque</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}>
            <TextField select fullWidth size="small" label="Tipo"
              value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
              <MenuItem value="RECIBIDO">Recibido (de cliente)</MenuItem>
              <MenuItem value="EMITIDO">Emitido (a proveedor)</MenuItem>
            </TextField>
            <TextField fullWidth size="small" label="N° Cheque"
              value={form.numero_cheque} onChange={e => setForm(f => ({ ...f, numero_cheque: e.target.value }))} />
            <TextField select fullWidth size="small" label="Banco"
              value={form.banco_id} onChange={e => setForm(f => ({ ...f, banco_id: e.target.value }))}>
              {bancos.map(b => <MenuItem key={b.id} value={b.id}>{b.nombre_banco}</MenuItem>)}
            </TextField>
            <TextField fullWidth size="small" label="Monto" type="number"
              value={form.monto} onChange={e => setForm(f => ({ ...f, monto: e.target.value }))}
              InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, fontSize: 13 }}>$</Typography> }} />
            <TextField fullWidth size="small" label="Fecha Emisión" type="date"
              value={form.fecha_emision} onChange={e => setForm(f => ({ ...f, fecha_emision: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <TextField fullWidth size="small" label="Fecha Cobro" type="date"
              value={form.fecha_cobro} onChange={e => setForm(f => ({ ...f, fecha_cobro: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <TextField fullWidth size="small" label="Beneficiario"
              value={form.beneficiario} onChange={e => setForm(f => ({ ...f, beneficiario: e.target.value }))}
              sx={{ gridColumn: '1 / -1' }} />
            <TextField fullWidth size="small" label="Concepto" multiline rows={2}
              value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))}
              sx={{ gridColumn: '1 / -1' }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={guardarCheque} disabled={saving || !form.numero_cheque || !form.monto}
            sx={{ bgcolor: '#e65100', textTransform: 'none' }}>
            {saving ? <CircularProgress size={16} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
